import { useState } from "react";

export type MostRecentOption = {
    value: string;
    selectedCount: number;
}

const parseValue = (value: unknown): string => {
    if (!value) return "";
    return String(value).trim();
}

const parseViews = (views: unknown): number => {
    if (typeof views !== "number") return 0;
    return views;
}

const getOptionsFromStorage = (key: string): MostRecentOption[] => {
    const localeStorageSupported = typeof window !== "undefined" && typeof window.localStorage !== "undefined";
    if (!localeStorageSupported) return [];
    const options = window.localStorage.getItem(key);
    if (!options) return [];
    try {
        const parsedOptions = JSON.parse(options);
        if (!Array.isArray(parsedOptions)) return [];
        return parsedOptions
            .map((option: unknown) => {
                if (typeof option !== "object" || option === null) return { value: "", selectedCount: 0 };
                return {
                    value: "value" in option ? parseValue(option.value) : "",
                    selectedCount: "selectedCount" in option ? parseViews(option.selectedCount) : 0,
                };
            })
            .filter((option) => option.value);
    } catch (err) {
        return [];
    }
}

export type UseMostRecentOptionsReturn = {
    options: MostRecentOption[];
    addOption: (value: string) => void;
    removeOption: (value: string) => void;
}

export const useMostRecentOptions = (storageKey: string, limit: number = 50): UseMostRecentOptionsReturn => {
    let initialOptions = getOptionsFromStorage(storageKey)
        .sort((a, b) => b.selectedCount - a.selectedCount)
        .slice(0, limit);

    const [options, setOptions] = useState<MostRecentOption[]>(initialOptions);

    const addOption = (value: string) => {
        const optionExists = options.find((option) => option.value === value);
        if (optionExists) {
            const newOptions = options.map((option) => {
                if (option.value === value) {
                    return {
                        ...option,
                        selectedCount: option.selectedCount + 1,
                    };
                }
                return option;
            });
            setOptions(newOptions);
            window.localStorage.setItem(storageKey, JSON.stringify(newOptions));
        } else {
            const newOptions = [{ value, selectedCount: 1 }, ...options].slice(0, limit);
            setOptions(newOptions);
            window.localStorage.setItem(storageKey, JSON.stringify(newOptions));
        }
    };

    const removeOption = (value: string) => {
        const newOptions = options.filter((option) => option.value !== value);
        setOptions(newOptions);
        window.localStorage.setItem(storageKey, JSON.stringify(newOptions));
    };

    return {
        options,
        addOption,
        removeOption,
    }
};